import React from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import type { ModalChildrenProps } from '@noths/polaris-client-ribbons-design-system';
import {
  HyperButtonLink,
  IconCheckmark,
  Modal,
  OutlineButton,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { priceFormatter } from '@noths/polaris-client-utils';
import { Button } from '@noths/polaris-design-system-core-components';
import getConfig from 'next/config';

import type { Basket } from 'src/types/Basket';
import * as styles from './MembershipBasketModal.styles';

const {
  publicRuntimeConfig: { MEMBERSHIP_PRODUCT_CODE },
} = getConfig();

export interface MembershipBasketModalProps {
  basket: Basket;
  isModalOpen: boolean;
  onModalClose: VoidFunction;
}

const BasketInfoAndCTAs = ({
  basket,
  onModalClose,
}: Pick<MembershipBasketModalProps, 'basket' | 'onModalClose'>) => {
  return (
    <>
      <div css={styles.basketInfo}>
        {basket.itemsTotal && (
          <p css={styles.basketInfoTotal}>
            {`Basket total: ${priceFormatter(basket.itemsTotal.currency, basket.itemsTotal.cents)}`}
          </p>
        )}
        {Boolean(basket.itemsCount) && (
          <p css={styles.basketInfoItems}>
            {`${basket!.itemsCount} ${'items'}`}
            <span css={visuallyHidden}> in the basket</span>
          </p>
        )}
      </div>
      <div css={styles.buttonContainer}>
        <OutlineButton fullWidth onClick={onModalClose}>
          Continue shopping
        </OutlineButton>
        <Button css={styles.goToBasketButton} href="/cart">
          Go to basket
        </Button>
      </div>
    </>
  );
};

export const MembershipBasketModal = ({
  basket,
  isModalOpen,
  onModalClose,
}: MembershipBasketModalProps) => {
  const isOnSmallScreen = useBreakpoint({ initializeWithLarge: true }) === Breakpoint.S;
  const membershipProduct =
    basket.items.find((item) => item.product.code === MEMBERSHIP_PRODUCT_CODE)?.product || null;

  if (membershipProduct === null) {
    return null;
  }

  const { images, partner, prices, title } = membershipProduct;

  return (
    <>
      <Modal
        appRootSelector="#NOTHSPageWrapper"
        contentLabel="Add to Basket Modal"
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
      >
        {({ Body, Footer, Header }: ModalChildrenProps) => (
          <>
            <Header notificationStatus="success" onCloseButtonClick={onModalClose}>
              <div css={[styles.addToBasketSection, styles.addToBasketHeaderSection]}>
                <IconCheckmark height={24} width={24} />
                <h2 css={styles.addToBasketHeader}>{'Item added to basket'}</h2>
              </div>
            </Header>
            <Body>
              <div>
                <div css={[styles.addToBasketSection, styles.addToBasketProductSection]}>
                  <div css={styles.productInfo}>
                    <img
                      alt="product"
                      css={styles.productImage}
                      height={135}
                      src={images.medium}
                      width={135}
                    />
                    <div css={[styles.hyperButtonLink]}>
                      <HyperButtonLink href="#">{partner.name}</HyperButtonLink>
                      <p css={styles.productDescription}>{title}</p>
                    </div>
                  </div>
                  <div css={styles.basketBreakdownWrapper}>
                    <div css={styles.priceBreakdownContainer}>
                      <p css={styles.priceBreakdownTotal}>Item price</p>{' '}
                      <p css={styles.priceBreakdownPrice}>
                        {priceFormatter(prices.listPrice.currency, prices.listPrice.cents)}
                      </p>
                    </div>
                    <div css={styles.basketBreakdownWrapper}>
                      <div css={styles.totalDivider}>
                        <div css={[styles.priceBreakdownContainer]}>
                          <p css={styles.totalLabel}>Total</p>
                          <p css={styles.totalPrice}>
                            {priceFormatter(prices.listPrice.currency, prices.listPrice.cents)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div css={[styles.addToBasketSection, styles.addToBasketButtonSection]}>
                  {!isOnSmallScreen && (
                    <BasketInfoAndCTAs basket={basket} onModalClose={onModalClose} />
                  )}
                </div>
              </div>
            </Body>
            {isOnSmallScreen && (
              <Footer>
                <div css={styles.footerItems}>
                  <BasketInfoAndCTAs basket={basket} onModalClose={onModalClose} />
                </div>
              </Footer>
            )}
          </>
        )}
      </Modal>
      <styles.AddToBasketModalGlobalStyles />
    </>
  );
};
